import { Capacitor } from "@capacitor/core";
import {
  AuthenticationApiFactory,
  Configuration,
  RailsLegacyV2ApiFactory,
  SmartHealthLinksApiFactory,
} from "@docket/consumer-app-client-typescript-axios";
import axios, { type AxiosInstance, isAxiosError } from "axios";
import { API_DOMAIN, PROTOCOL } from "../globals";
import { appVersion } from "../utils/app-version";
import { toApiError } from "./errors";
import {
  dbTokenStore,
  RefreshingTokenProvider,
  SessionEndedError,
  type TokenStore,
} from "./tokens";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    /** Set on the one retry after a 401, so a second 401 fails instead of looping. */
    _retriedAfterRefresh?: boolean;
    /** The session (RefreshingTokenProvider.session) the request was made in. */
    _session?: number;
  }
}

async function userAgent(): Promise<string> {
  if (Capacitor.isNativePlatform()) {
    return `DocketCapacitor/1.0-${await appVersion()} ${Capacitor.getPlatform()}`;
  }
  return `DocketPWA/1.0-${__APP_VERSION__}`;
}

export interface ApiOptions {
  tokenStore?: TokenStore;
  /** Called when the tokens can't be refreshed any more. */
  onAuthFailure?: () => void;
}

/**
 * The generated Docket API clients, built once: one configuration, one authenticated axios instance
 * and one token provider, so concurrent requests share a single token refresh.
 */
export function createApi({
  tokenStore = dbTokenStore,
  onAuthFailure = () => {},
}: ApiOptions = {}) {
  const basePath = `${PROTOCOL}://${API_DOMAIN}`;
  let userAgentHeader: Promise<string> | undefined;

  const withUserAgent = (instance: AxiosInstance): AxiosInstance => {
    instance.interceptors.request.use(async (config) => {
      userAgentHeader ??= userAgent();
      config.headers.set("X-User-Agent", await userAgentHeader);
      return config;
    });
    return instance;
  };
  const rejectWithApiErrors = (instance: AxiosInstance): void => {
    instance.interceptors.response.use(undefined, (err: unknown) => {
      throw toApiError(err);
    });
  };

  // The auth API is what hands out tokens, so its requests carry none.
  const authAxios = withUserAgent(axios.create());
  rejectWithApiErrors(authAxios);
  const auth = AuthenticationApiFactory(new Configuration({ basePath }), undefined, authAxios);

  const tokenProvider = new RefreshingTokenProvider(auth, tokenStore, { onAuthFailure });

  const authedAxios = withUserAgent(axios.create());
  // Each request keeps the session its token is from (tokens() gives none to a session that ends while
  // it reads them); its retry keeps the same one.
  authedAxios.interceptors.request.use((config) => {
    config._session ??= tokenProvider.session;
    return config;
  });
  // The provider refreshes tokens before they expire. A 401 anyway (a revoked or otherwise broken
  // token) refreshes once and retries the request once, unless the session it was made in has
  // ended: then it would go out as whoever signed in next.
  authedAxios.interceptors.response.use(undefined, async (err: unknown) => {
    const config = isAxiosError(err) ? err.config : undefined;
    const sentAccessToken = String(config?.headers.get("Authorization") ?? "").startsWith("Bearer");
    if (
      isAxiosError(err) &&
      err.response?.status === 401 &&
      config &&
      sentAccessToken &&
      !config._retriedAfterRefresh
    ) {
      if (config._session !== tokenProvider.session) {
        throw new SessionEndedError({ cause: err });
      }
      const tokens = await tokenProvider.refreshTokens();
      if (config._session !== tokenProvider.session) {
        throw new SessionEndedError({ cause: err });
      }
      config._retriedAfterRefresh = true;
      config.headers.set("Authorization", `Bearer ${tokens.access}`);
      return authedAxios.request(config);
    }
    throw err;
  });
  rejectWithApiErrors(authedAxios);

  const authedConfiguration = new Configuration({
    basePath,
    accessToken: async () => (await tokenProvider.tokens()).access,
  });

  return {
    auth,
    railsLegacyV2: RailsLegacyV2ApiFactory(authedConfiguration, undefined, authedAxios),
    smartHealthLinks: SmartHealthLinksApiFactory(authedConfiguration, undefined, authedAxios),
    tokenProvider,
    /** The underlying instances, for tests to mock. */
    axiosInstances: { auth: authAxios, authed: authedAxios },
  };
}

export type Api = ReturnType<typeof createApi>;

let authFailureHandler: () => void = () => {};

/** Register what happens when the session can't be kept alive (the session signs the user out). */
export function onAuthFailure(handler: () => void): void {
  authFailureHandler = handler;
}

export const api: Api = createApi({ onAuthFailure: () => authFailureHandler() });
